import type { ReactNode } from "react";
import { FileCode2, FileText, ImageIcon } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { useChatFileDrop } from "../hooks/use-chat-file-drop";

const FileTypeIcon = ({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) => (
  <div
    className={cn(
      "flex h-12 w-12 items-center justify-center rounded-2xl border sm:h-14 sm:w-14",
      "border-indigo-200 bg-indigo-50 text-indigo-600 shadow-sm",
      "dark:border-indigo-800 dark:bg-indigo-950/80 dark:text-indigo-300",
      className
    )}
  >
    {children}
  </div>
);

export const ChatDropOverlay = () => {
  const { t } = useTranslation();
  const { isVisible, isProcessing, remainingSlots } = useChatFileDrop();

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          key="chat-drop-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          className="pointer-events-none fixed inset-0 z-[70] flex items-center justify-center px-4"
          aria-hidden={!isVisible}
        >
          <div className="absolute inset-0 bg-bg-primary/80 dark:bg-gray-900/80 backdrop-blur-sm" />

          <div className="relative flex w-full max-w-sm flex-col items-center text-center">
            <div className="mb-5 flex items-end justify-center sm:mb-6">
              <FileTypeIcon className="-mr-2 -rotate-12">
                <FileCode2 className="h-5 w-5 sm:h-6 sm:w-6" />
              </FileTypeIcon>
              <FileTypeIcon className="z-10 scale-110">
                <ImageIcon className="h-5 w-5 sm:h-6 sm:w-6" />
              </FileTypeIcon>
              <FileTypeIcon className="-ml-2 rotate-12">
                <FileText className="h-5 w-5 sm:h-6 sm:w-6" />
              </FileTypeIcon>
            </div>

            <p className="mb-1 text-sm font-medium text-text-secondary dark:text-gray-400">
              {isProcessing
                ? t("chat.dropOverlay.uploading")
                : remainingSlots > 0
                  ? t("chat.dropOverlay.remaining", { count: remainingSlots })
                  : "\u00a0"}
            </p>

            <h2 className="text-xl font-semibold text-text-primary dark:text-gray-100 sm:text-2xl">
              {remainingSlots <= 0 && !isProcessing
                ? t("chat.dropOverlay.maxReached")
                : t("chat.dropOverlay.title")}
            </h2>

            <p className="mt-2 text-sm text-text-tertiary dark:text-gray-400">
              {t("chat.dropOverlay.subtitle")}
            </p>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
